/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useMutation } from 'react-query';
import type { MutationFunction, UseMutationOptions } from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { BEResponseBoolean } from '../schemas/bEResponseBoolean.ts';
import type { CambioPassword } from '../schemas/cambioPassword.ts';
import type { Login } from '../schemas/login.ts';
import type { LoginResponse } from '../schemas/loginResponse.ts';
import type { RecuperoPassword } from '../schemas/recuperoPassword.ts';
import type { RecuperoPasswordEmail } from '../schemas/recuperoPasswordEmail.ts';
import type { Registrazione } from '../schemas/registrazione.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const putExternalVerificaIdKey = (
  id: string,
  key: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseBoolean>(
    {
      url: `/procedimenti-authentication-manager/external/verifica/${id}/${key}`,
      method: 'put',
    },
    options,
  );
};

export const getPutExternalVerificaIdKeyMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putExternalVerificaIdKey>>,
    TError,
    { id: string; key: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putExternalVerificaIdKey>>,
  TError,
  { id: string; key: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putExternalVerificaIdKey>>,
    { id: string; key: string }
  > = props => {
    const { id, key } = props ?? {};

    return putExternalVerificaIdKey(id, key, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutExternalVerificaIdKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof putExternalVerificaIdKey>>
>;

export type PutExternalVerificaIdKeyMutationError = ErrorType<unknown>;

export const usePutExternalVerificaIdKey = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putExternalVerificaIdKey>>,
    TError,
    { id: string; key: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutExternalVerificaIdKeyMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Verifica che id e key combacino e cambia la password
 * @summary Verifica recupera password
 */
export const postExternalVerificaPasswordIdKey = (
  id: string,
  key: string,
  recuperoPassword: RecuperoPassword,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseBoolean>(
    {
      url: `/procedimenti-authentication-manager/external/verifica-password/${id}/${key}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: recuperoPassword,
    },
    options,
  );
};

export const getPostExternalVerificaPasswordIdKeyMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalVerificaPasswordIdKey>>,
    TError,
    { id: string; key: string; data: RecuperoPassword },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postExternalVerificaPasswordIdKey>>,
  TError,
  { id: string; key: string; data: RecuperoPassword },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postExternalVerificaPasswordIdKey>>,
    { id: string; key: string; data: RecuperoPassword }
  > = props => {
    const { id, key, data } = props ?? {};

    return postExternalVerificaPasswordIdKey(id, key, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalVerificaPasswordIdKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof postExternalVerificaPasswordIdKey>>
>;
export type PostExternalVerificaPasswordIdKeyMutationBody = RecuperoPassword;
export type PostExternalVerificaPasswordIdKeyMutationError = ErrorType<unknown>;

/**
 * @summary Verifica recupera password
 */
export const usePostExternalVerificaPasswordIdKey = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalVerificaPasswordIdKey>>,
    TError,
    { id: string; key: string; data: RecuperoPassword },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostExternalVerificaPasswordIdKeyMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Invia una email per recuperare la password
 * @summary Recupera password
 */
export const postExternalRecuperoPassword = (
  recuperoPasswordEmail: RecuperoPasswordEmail,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseBoolean>(
    {
      url: `/procedimenti-authentication-manager/external/recupero-password`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: recuperoPasswordEmail,
    },
    options,
  );
};

export const getPostExternalRecuperoPasswordMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalRecuperoPassword>>,
    TError,
    { data: RecuperoPasswordEmail },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postExternalRecuperoPassword>>,
  TError,
  { data: RecuperoPasswordEmail },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postExternalRecuperoPassword>>,
    { data: RecuperoPasswordEmail }
  > = props => {
    const { data } = props ?? {};

    return postExternalRecuperoPassword(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalRecuperoPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof postExternalRecuperoPassword>>
>;
export type PostExternalRecuperoPasswordMutationBody = RecuperoPasswordEmail;
export type PostExternalRecuperoPasswordMutationError = ErrorType<unknown>;

/**
 * @summary Recupera password
 */
export const usePostExternalRecuperoPassword = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalRecuperoPassword>>,
    TError,
    { data: RecuperoPasswordEmail },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostExternalRecuperoPasswordMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Se primo accesso cambia la password
 * @summary Login
 */
export const postExternalLogin = (
  login: Login,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<LoginResponse>(
    {
      url: `/procedimenti-authentication-manager/external/login`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: login,
    },
    options,
  );
};

export const getPostExternalLoginMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalLogin>>,
    TError,
    { data: Login },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postExternalLogin>>,
  TError,
  { data: Login },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postExternalLogin>>,
    { data: Login }
  > = props => {
    const { data } = props ?? {};

    return postExternalLogin(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof postExternalLogin>>
>;
export type PostExternalLoginMutationBody = Login;
export type PostExternalLoginMutationError = ErrorType<unknown>;

/**
 * @summary Login
 */
export const usePostExternalLogin = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalLogin>>,
    TError,
    { data: Login },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostExternalLoginMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Invia una email con la password
 * @summary Crea un utenza nel database
 */
export const postExternalCrea = (
  registrazione: Registrazione,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseBoolean>(
    {
      url: `/procedimenti-authentication-manager/external/crea`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: registrazione,
    },
    options,
  );
};

export const getPostExternalCreaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalCrea>>,
    TError,
    { data: Registrazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postExternalCrea>>,
  TError,
  { data: Registrazione },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postExternalCrea>>,
    { data: Registrazione }
  > = props => {
    const { data } = props ?? {};

    return postExternalCrea(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalCreaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postExternalCrea>>
>;
export type PostExternalCreaMutationBody = Registrazione;
export type PostExternalCreaMutationError = ErrorType<unknown>;

/**
 * @summary Crea un utenza nel database
 */
export const usePostExternalCrea = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalCrea>>,
    TError,
    { data: Registrazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostExternalCreaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Permette di cambiare la password
 * @summary Cambio password
 */
export const postExternalCambioPassword = (
  cambioPassword: CambioPassword,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/procedimenti-authentication-manager/external/cambio-password`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: cambioPassword,
    },
    options,
  );
};

export const getPostExternalCambioPasswordMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalCambioPassword>>,
    TError,
    { data: CambioPassword },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postExternalCambioPassword>>,
  TError,
  { data: CambioPassword },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postExternalCambioPassword>>,
    { data: CambioPassword }
  > = props => {
    const { data } = props ?? {};

    return postExternalCambioPassword(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalCambioPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof postExternalCambioPassword>>
>;
export type PostExternalCambioPasswordMutationBody = CambioPassword;
export type PostExternalCambioPasswordMutationError = ErrorType<unknown>;

/**
 * @summary Cambio password
 */
export const usePostExternalCambioPassword = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postExternalCambioPassword>>,
    TError,
    { data: CambioPassword },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostExternalCambioPasswordMutationOptions(options);

  return useMutation(mutationOptions);
};
